import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateToYYYYMMDD } from '@/utils';
import { colors, semanticColors } from '@/assets/styles/colors';
import { zIndex } from '@/assets/styles';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { Calendar, formatDateDisplay, addMonths } from './calendar';
import { useCalendarPanel } from '@/hooks/ui/filters/calendar';
import type { DateRangeValue } from './DateRangePicker';


export type { DateRangeValue };

export interface DateRangeFilterProps {
  className?: string;
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  onChange?: (range: DateRangeValue) => void;
  onOpenChange?: (open: boolean) => void;
  closeOnComplete?: boolean;
  closeOnOutsideClick?: boolean;
  maxDate?: string;
  minDate?: string;
  maxRangeDays?: number;
  placeholder?: string;
  /**
   * Desfase (en meses) del mes base mostrado al abrir el panel sin un rango
   * seleccionado. El calendario izquierdo muestra `mesActual + offset` y el
   * derecho `mesActual + offset + 1`. Por defecto `0` (mes actual + siguiente);
   * usar `-1` para mostrar mes anterior + mes actual.
   */
  initialMonthOffset?: number;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  className = '',
  value,
  defaultValue,
  onChange,
  onOpenChange,
  closeOnComplete = true,
  closeOnOutsideClick = true,
  maxDate,
  minDate,
  maxRangeDays,
  placeholder = 'Seleccionar fecha',
  initialMonthOffset = 0,
}) => {
  const isControlled = typeof value === 'object' && value !== null;
  const [internalRange, setInternalRange] = useState<DateRangeValue | undefined>(defaultValue);
  const rangeValue = useMemo(
    () => isControlled ? value : (internalRange || {}),
    [isControlled, value, internalRange],
  );
  const { open, toggle, close, triggerRef, panelRef } = useCalendarPanel({
    closeOnOutsideClick,
    onOpenChange,
  });

  const [baseMonth, setBaseMonth] = useState(() => addMonths(new Date(), initialMonthOffset));
  const [draft, setDraft] = useState<DateRangeValue | undefined>(undefined);

  const pendingFrom = draft?.from && !draft?.to ? draft.from : undefined;

  const effectiveMaxDate = useMemo(() => {
    if (!pendingFrom || !maxRangeDays) return maxDate;
    const d = new Date(pendingFrom);
    d.setDate(d.getDate() + maxRangeDays);
    return formatDateToYYYYMMDD(d);
  }, [pendingFrom, maxRangeDays, maxDate]);

  const effectiveMinDate = useMemo(() => {
    if (!pendingFrom || !maxRangeDays) return minDate;
    const d = new Date(pendingFrom);
    d.setDate(d.getDate() - maxRangeDays);
    return formatDateToYYYYMMDD(d);
  }, [pendingFrom, maxRangeDays, minDate]);

  const displayRange: DateRangeValue = open && draft
    ? draft
    : ((rangeValue.from || rangeValue.to) ? rangeValue : (defaultValue ?? {}));

  useEffect(() => {
    if (!open) setDraft(undefined);
  }, [open]);

  const emittedDefaultRef = useRef(false);
  useEffect(() => {
    if (!isControlled && defaultValue && !emittedDefaultRef.current) {
      setInternalRange(prev => prev ?? defaultValue);
      if (defaultValue.from && defaultValue.to) onChange?.(defaultValue);
      emittedDefaultRef.current = true;
    }
  }, [isControlled, defaultValue, onChange]);

  useEffect(() => {
    const fromIso = (isControlled ? value?.from : internalRange?.from) ?? defaultValue?.from;
    if (fromIso) {
      const [year, month] = fromIso.split('-').map(Number);
      if (!Number.isNaN(year) && !Number.isNaN(month)) setBaseMonth(new Date(year, month - 1, 1));
    }
  }, [isControlled, value?.from, internalRange?.from, defaultValue?.from]);

  useEffect(() => {
    if (isControlled && defaultValue && !emittedDefaultRef.current) {
      const emptyControlled = !value?.from && !value?.to;
      if (emptyControlled && defaultValue.from && defaultValue.to) {
        onChange?.(defaultValue);
        emittedDefaultRef.current = true;
      }
    }
  }, [isControlled, value?.from, value?.to, defaultValue, onChange]);

  const handleDayClick = useCallback((date: Date) => {
    const clicked = formatDateToYYYYMMDD(date);
    setDraft(prev => {
      const current = prev ?? rangeValue;
      let next: DateRangeValue;
      if (!current.from || (current.from && current.to)) {
        next = { from: clicked, to: undefined };
      } else {
        if (clicked < current.from) next = { from: clicked, to: current.from };
        else if (clicked === current.from) next = { from: clicked, to: clicked };
        else next = { from: current.from, to: clicked };
      }
      if (next.from && next.to) {
        if (!isControlled) setInternalRange(next);
        onChange?.(next);
        if (closeOnComplete) {
          setTimeout(() => {
            setDraft(undefined);
            close();
          }, 0);
        }
      }
      return next;
    });
  }, [rangeValue, isControlled, onChange, closeOnComplete, close]);

  const buttonText = displayRange.from && displayRange.to
    ? `${formatDateDisplay(displayRange.from)} - ${formatDateDisplay(displayRange.to)}`
    : placeholder;

  const buttonBg = colors.primary.background;
  const textColor = colors.primary.dash;
  const rightIconColor = colors.important.main;
  const leftIconColor = colors.primary.dash;

  return (
    <div className={`relative inline-block ${className}`} ref={triggerRef}>
      <PrimaryButton
        customVariant={{
          default: {
            backgroundColor: buttonBg,
            textColor: textColor,
          },
          hover: {
            backgroundColor: colors.primary.background100 || buttonBg,
            textColor: textColor,
          },
          pressed: {
            backgroundColor: colors.primary.background200 || buttonBg,
            textColor: textColor,
          },
          focus: {
            backgroundColor: colors.primary.background100 || buttonBg,
            textColor: textColor,
          },
        }}
        iconLeft={<IconScout name="calendar" color={leftIconColor} />}
        iconRight={
          <IconScout
            name="angleDown"
            color={rightIconColor}
            className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        }
        onClick={toggle}
        className="!rounded-[16px]"
        textVariant="subheader-regular"
      >
        {buttonText}
      </PrimaryButton>

      {open && (
        <div
          ref={panelRef}
          className="absolute left-0 mt-2 rounded shadow-md border p-3 min-w-[520px]"
          style={{
            backgroundColor: semanticColors.background.primary,
            borderColor: semanticColors.border.light,
            zIndex: zIndex.dropdown,
          }}
        >
          <div className="grid grid-cols-2 gap-6">
            <Calendar
              baseMonth={baseMonth}
              onMonthChange={setBaseMonth}
              selectedDates={[displayRange.from, displayRange.to].filter(Boolean) as string[]}
              highlightRange={displayRange}
              onDayClick={handleDayClick}
              showNavigation="left"
              maxDate={effectiveMaxDate}
              minDate={effectiveMinDate}
            />
            <Calendar
              baseMonth={addMonths(baseMonth, 1)}
              onMonthChange={(date) => setBaseMonth(addMonths(date, -1))}
              selectedDates={[displayRange.from, displayRange.to].filter(Boolean) as string[]}
              highlightRange={displayRange}
              onDayClick={handleDayClick}
              showNavigation="right"
              maxDate={effectiveMaxDate}
              minDate={effectiveMinDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
